import { createContext, ReactNode, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '../lib/i18n';
import { useGenericBi } from '../lib/use-generic-bi';
import { Modal } from './Modal';
import { Button, Typography } from './symphony/ui';

export interface ConfirmRequest {
  title?: string;
  message?: string;
  /** Names the action ("Disconnect", "Remove") — never a bare "OK". */
  confirmLabel: string;
  /** Names the way out when "Cancel" would be vague ("Keep Alfred"); defaults to Cancel. */
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Where the dialog was raised. Its confirm / cancel / close are logged as modal actions under these.
   * `null` means the caller already reports the answer itself under a cross-surface contract (the
   * compliance tap/confirm pair, docs/compliance/bi.md §4), and a second clickCta would double-count it.
   * Required rather than optional so every caller makes that call deliberately.
   */
  bi: { page_name: string; page_version?: string; modal_name: string } | null;
}

export type Confirm = (request: ConfirmRequest) => Promise<boolean>;

interface Pending {
  request: ConfirmRequest;
  resolve: (ok: boolean) => void;
}

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * The app's yes/no confirm, in place of `window.confirm`. The browser dialog cannot take our tokens or
 * a named action button, and it shows the site's domain as its title ("symphony.wix.com says").
 *
 * A promise rather than per-screen dialog state, because most callers are hooks deep in a flow
 * (disconnect, a dashboard widget's postMessage) that have no render output of their own to put a
 * dialog in. Mounted once in `_app.tsx`.
 */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm() needs a <ConfirmProvider> above it (mounted in pages/_app.tsx).');
  }
  return confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  // The ref is the source of truth for "who is waiting": a second request must settle the first, and
  // reading that from state would see a stale value inside a callback created before the first render.
  const pendingRef = useRef<Pending | null>(null);

  const settle = useCallback((ok: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(ok);
  }, []);

  const confirm = useCallback<Confirm>(
    (request) =>
      new Promise<boolean>((resolve) => {
        // One dialog at a time. A newer request replaces an open one, which counts as declined, so no
        // caller is left awaiting a promise that never settles.
        pendingRef.current?.resolve(false);
        pendingRef.current = { request, resolve };
        setPending(pendingRef.current);
      }),
    [],
  );

  useEffect(() => () => pendingRef.current?.resolve(false), []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog request={pending.request} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ request, onSettle }: { request: ConfirmRequest; onSettle: (ok: boolean) => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const titleId = useId();
  const messageId = useId();
  // `cancel` is the Cancel button; `close` is the ✕, Escape or a scrim click — the split SignBaaModal reports.
  const answer = (action: 'confirm' | 'cancel' | 'close') => {
    if (request.bi) {
      logModalActionBi({ ...request.bi, action });
    }
    onSettle(action === 'confirm');
  };

  return (
    <Modal
      open
      onClose={() => answer('close')}
      width={440}
      maxHeightPx={650}
      scrollBody
      title={request.title}
      // The shell names the dialog by its title row. A confirm with no title (connector disconnect, whose
      // message already opens with the question) is named by the message instead, so it is never unnamed.
      labelledBy={request.title ? titleId : messageId}
      footer={
        <>
          <Button variant="outlined" onClick={() => answer('cancel')}>
            {request.cancelLabel ?? t('common.cancel')}
          </Button>
          <Button color={request.destructive ? 'destructive' : 'accent'} onClick={() => answer('confirm')}>
            {request.confirmLabel}
          </Button>
        </>
      }
    >
      {request.message && (
        <Typography id={messageId} variant="p2" color="secondary" component="p">
          {request.message}
        </Typography>
      )}
    </Modal>
  );
}
